import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, MapPin, CheckCircle2, ChevronDown,
  Home, Waves, UtensilsCrossed, TreePine, Sprout, Building2,
  TrendingUp, CalendarCheck, ShieldCheck, BarChart3, Users,
  Clock, ArrowRight, Star, Zap, Target, Award, Lock
} from 'lucide-react';
import { apiService } from '../services/api';

/* ------------------------------------------------------------------ */
/* Intersection observer for fade-in                                   */
/* ------------------------------------------------------------------ */
function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

/* FadeIn wrapper */
const FadeIn: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children, delay = 0, className = ''
}) => {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* FAQ Accordion                                                       */
/* ------------------------------------------------------------------ */
const faqData = [
  {
    q: 'What are the investment options?',
    a: 'Ready bungalow (rental income) or land + cottage + farming (dual income).',
  },
  {
    q: 'How do I earn returns?',
    a: 'Through rental income and optional farming income.',
  },
  {
    q: 'Is the property managed?',
    a: 'Yes, completely managed.',
  },
  {
    q: 'When does income start?',
    a: 'Post project activation.',
  },
  {
    q: 'Is this good for small investors?',
    a: 'Yes, entry-level options are available.',
  },
];

const FAQ: React.FC = () => {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      {faqData.map((item, i) => (
        <div
          key={i}
          className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between px-6 py-5 text-left"
          >
            <span className="font-semibold text-gray-900 text-lg">
              Q{i + 1}: {item.q}
            </span>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
            />
          </button>
          <div
            className="overflow-hidden transition-all duration-400"
            style={{
              maxHeight: open === i ? '200px' : '0',
              opacity: open === i ? 1 : 0,
            }}
          >
            <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main Page Component                                                 */
/* ------------------------------------------------------------------ */
export const LonavalaPage: React.FC = () => {
  const navigate = useNavigate();
  /* form state */
  const [formData, setFormData] = useState({
    name: '', phone: '', city: '', email: '',
    investment_interest: 'Rental Income Only',
    budget_range: 'Rs.25 Lakh-Rs.50 Lakhs',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiService.submitLonavalaInquiry(formData);
      if (response.success) {
        navigate('/lonavala/thank-you');
        setFormData({
          name: '', phone: '', city: '', email: '',
          investment_interest: 'Rental Income Only',
          budget_range: 'Rs.25 Lakh-Rs.50 Lakhs',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('lonavala-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  /* ---------------------------------------------------------------- */
  /* RENDER                                                            */
  /* ---------------------------------------------------------------- */
  return (
    <div className="bg-gray-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* ═══════════════════════════ HERO ═══════════════════════════ */}
      <section
        className="relative min-h-[90vh] flex items-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(15,15,30,0.92) 0%, rgba(30,10,10,0.88) 100%)',
        }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://cf.bstatic.com/xdata/images/hotel/max1024x768/306739107.jpg?k=984dd89956782a7d4797515a2490ce447cdf1f9868d2d67ee46988c71b0fcfb3&o=)`,
            filter: 'brightness(0.35)',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          {/* Tag */}
          <FadeIn>
            <span className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold mb-8 border border-yellow-500/30">
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              Pre-Launch Project • Limited Inventory
            </span>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight mb-6">
              You invest in land.
              <br />
              <span className="text-yellow-400">We deliver income.</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mb-10 leading-relaxed">
              Invest in a premium Lonavala resort project and earn through rental
              income and farming — with a fully managed model.
            </p>
          </FadeIn>

          {/* Badges */}
          <FadeIn delay={0.3}>
            <div className="flex flex-wrap gap-3 mb-10">
              {[
                'Monthly income potential',
                'Dual income model (Rent + Farming)',
                'Professionally managed',
              ].map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-4 py-2.5 rounded-lg text-sm font-medium"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  {t}
                </span>
              ))}
            </div>
          </FadeIn>

          {/* CTAs */}
          <FadeIn delay={0.4}>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToForm}
                className="group flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
              >
                <CalendarCheck className="w-5 h-5" />
                Book Site Visit
              </button>
              <button
                onClick={scrollToForm}
                className="group flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-lg font-bold text-lg shadow-xl hover:bg-red-700 hover:scale-[1.02] transition-all duration-300"
              >
                Get Investment Details
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </FadeIn>

          <FadeIn delay={0.5}>
            <p className="mt-8 text-gray-400 text-sm">
              Pre-Launch Project • Limited Inventory • High Growth Location
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════ INCOME ASSET SECTION ═══════════ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
                Turn Your Investment into a Monthly
                <br className="hidden sm:block" /> Income Asset
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                Choose how you want to earn — rental income or dual income from rent + farming.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                color: 'bg-red-50 text-red-600',
                title: 'Rental Income',
                desc: 'Earn from resort stays and bookings',
              },
              {
                icon: Sprout,
                color: 'bg-green-50 text-green-600',
                title: 'Farming Income',
                desc: 'Earn from managed agricultural produce',
              },
              {
                icon: TrendingUp,
                color: 'bg-indigo-50 text-indigo-600',
                title: 'Capital Appreciation',
                desc: "Benefit from Lonavala's growing property demand",
              },
            ].map((card, i) => (
              <FadeIn key={card.title} delay={i * 0.1}>
                <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center">
                  <div className={`w-16 h-16 ${card.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <card.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-gray-500">{card.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ INVESTMENT MODEL ═══════════ */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 text-center mb-16">
              Choose Your Investment Model
            </h2>
          </FadeIn>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Option 1 */}
            <FadeIn delay={0.1}>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-52 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <img
                    src="https://i.ytimg.com/vi/OjjP_A80GR8/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLCWbAXvIz1aYd6ByHCDzKlp2ZuJjw"
                    alt="Ready Bungalow"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase mb-4">
                    Option 1
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Ready Bungalow / Fractional Ownership
                  </h3>
                  <p className="text-gray-500 mb-6">
                    <strong>Best For:</strong> Passive Income Investors
                  </p>

                  <p className="font-semibold text-gray-800 mb-3">What You Get:</p>
                  <ul className="space-y-2 mb-6">
                    {[
                      'Ownership in a ready bungalow (fractional model)',
                      'Monthly rental income from resort operations',
                      'Fully managed property',
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2 text-gray-600">
                        <CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        {t}
                      </li>
                    ))}
                  </ul>

                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Income Type:</p>
                    <span className="flex items-center gap-1 text-red-600 font-medium">
                      <CheckCircle2 className="w-4 h-4" /> Rental Income
                    </span>
                  </div>

                  <button
                    onClick={scrollToForm}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3.5 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    Explore This Option <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </FadeIn>

            {/* Option 2 */}
            <FadeIn delay={0.2}>
              <div className="relative bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 rounded-2xl shadow-xl overflow-hidden text-white">
                {/* Most Popular tag */}
                <span className="absolute top-4 right-4 bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg z-10">
                  Most Popular
                </span>
                <div className="h-52">
                  <img
                    src="https://gos3.ibcdn.com/655668f1-7c8d-4f28-a67b-3f0d1ae79f61.jpg"
                    alt="Land + Cottage + Farming"
                    className="w-full h-full object-cover opacity-80"
                  />
                </div>
                <div className="p-8">
                  <span className="inline-block bg-green-400/20 text-green-300 px-3 py-1 rounded-full text-xs font-bold uppercase mb-4">
                    Option 2
                  </span>
                  <h3 className="text-2xl font-bold mb-1">
                    Land + Cottage + Farming Model
                  </h3>
                  <p className="text-indigo-200 mb-6">
                    <strong>Best For:</strong> Higher returns & dual income
                  </p>

                  <p className="font-semibold mb-3">What You Get:</p>
                  <ul className="space-y-2 mb-6">
                    {['Land ownership', 'Cottage development', 'Farming setup (managed)'].map((t) => (
                      <li key={t} className="flex items-start gap-2 text-indigo-100">
                        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        {t}
                      </li>
                    ))}
                  </ul>

                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-6">
                    <p className="text-sm font-semibold mb-1">Income Type:</p>
                    <div className="space-y-1">
                      <span className="flex items-center gap-1 text-green-300 font-medium">
                        <CheckCircle2 className="w-4 h-4" /> Rental Income
                      </span>
                      <span className="flex items-center gap-1 text-green-300 font-medium">
                        <CheckCircle2 className="w-4 h-4" /> Farming Income
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={scrollToForm}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 py-3.5 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    Explore Dual Income Option <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════ WHY THIS INVESTMENT STANDS OUT ═══════════ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 text-center mb-16">
              Why This Investment Stands Out
            </h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BarChart3, text: 'Earn from 2 income streams (Rent + Farming)' },
              { icon: Zap, text: 'Entry at early pre-launch pricing' },
              { icon: Users, text: 'Fully managed operations' },
              { icon: Target, text: 'Low investment entry options' },
              { icon: Star, text: 'Designed for both income and lifestyle use' },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-100 hover:border-red-200 hover:shadow-md transition-all duration-300">
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="text-gray-800 font-medium">{item.text}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ LOCATION SECTION ═══════════ */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=800"
                  alt="Lonavala scenic view"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-8">
                  A Location That Drives Demand
                </h2>
                <ul className="space-y-5">
                  {[
                    'Top weekend destination near Mumbai & Pune',
                    'High rental demand throughout the year',
                    'Strong tourism-driven income potential',
                    'Scenic natural surroundings',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-gray-700">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="w-4 h-4 text-red-600" />
                      </div>
                      <span className="text-lg">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════ PREMIUM RESORT EXPERIENCE ═══════════ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-2">
                Premium Resort Living Experience
              </h2>
              <div className="w-16 h-1 bg-red-600 mx-auto mt-4 rounded-full" />
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Home, text: 'Luxury Cottages & Bungalows' },
              { icon: Waves, text: 'Swimming Pool' },
              { icon: UtensilsCrossed, text: 'Restaurant & Café' },
              { icon: TreePine, text: 'Landscaped Gardens' },
              { icon: Sprout, text: 'Farming & Experience Zone' },
              { icon: Building2, text: 'Resort Infrastructure' },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-red-600" />
                  </div>
                  <p className="font-semibold text-gray-800">{item.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SIMPLE MANAGED INCOME MODEL (Steps 1-6) ═══════════ */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 text-center mb-16">
              Simple, Managed Income Model
            </h2>
          </FadeIn>
          <div className="max-w-3xl mx-auto space-y-0">
            {[
              { num: 1, text: 'You invest in your preferred model' },
              { num: 2, text: 'Property + infrastructure is developed' },
              { num: 3, text: 'Resort operations begin' },
              { num: 4, text: 'Guests generate rental revenue' },
              { num: 5, text: 'Farming generates additional income (if selected)' },
              { num: 6, text: 'You earn monthly returns' },
            ].map((step, i) => (
              <FadeIn key={step.num} delay={i * 0.1}>
                <div className="flex items-center gap-6 py-5 border-b border-gray-200 last:border-b-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-lg"
                    style={{
                      background: `linear-gradient(135deg, ${i < 2 ? '#dc2626' : i < 4 ? '#f59e0b' : '#16a34a'}, ${i < 2 ? '#991b1b' : i < 4 ? '#d97706' : '#15803d'})`,
                    }}
                  >
                    {step.num}
                  </div>
                  <p className="text-lg font-medium text-gray-800">{step.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ BUILT FOR INCOME ═══════════ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 text-center mb-16">
              Built for Income from Day One
            </h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: 'ROI within 12 months*', sub: 'Quick returns on investment' },
              { icon: TrendingUp, title: 'Monthly income potential', sub: 'Regular cash flow' },
              { icon: BarChart3, title: 'Multiple income streams', sub: 'Diversified earnings' },
              { icon: Award, title: 'Long-term appreciation', sub: 'Growing asset value' },
            ].map((card, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white overflow-hidden group hover:scale-[1.03] transition-transform duration-300">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/10 rounded-full -translate-y-8 translate-x-8 group-hover:scale-150 transition-transform duration-500" />
                  <card.icon className="w-10 h-10 text-red-400 mb-4" />
                  <h3 className="text-lg font-bold mb-1">{card.title}</h3>
                  <p className="text-gray-400 text-sm">{card.sub}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.4}>
            <p className="text-center text-gray-400 text-sm mt-8 italic">
              *Returns are indicative and depend on operations & market performance.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════ FLEXIBLE INVESTMENT OPTIONS ═══════════ */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <FadeIn>
              <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-100">
                <div className="w-14 h-14 bg-yellow-50 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-yellow-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Flexible Investment Options
                </h3>
                <ul className="space-y-4">
                  {[
                    'Low entry investment options available',
                    'Multiple ownership formats',
                    'Structured payment plans',
                    'Designed for both small and large investors',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-100">
                <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mb-6">
                  <ShieldCheck className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  100% Managed. Zero Hassle.
                </h3>
                <ul className="space-y-4">
                  {[
                    'Property management handled by experts',
                    'Guest bookings & operations managed',
                    'Farming handled by professionals',
                    'Maintenance & upkeep included',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════ SECURE INVESTMENT + STRATEGIC LOCATION ═══════════ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <FadeIn>
              <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-10 text-white shadow-xl">
                <Lock className="w-10 h-10 text-indigo-300 mb-6" />
                <h3 className="text-2xl font-bold mb-6">
                  Secure & Structured Investment
                </h3>
                <ul className="space-y-4">
                  {[
                    'Transparent ownership structure',
                    'Defined investment model',
                    'Exit options available (as per agreement)',
                    'Designed for investor security',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-indigo-100">
                      <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-10 text-white shadow-xl">
                <MapPin className="w-10 h-10 text-red-200 mb-6" />
                <h3 className="text-2xl font-bold mb-6">
                  Strategically Located in Lonavala
                </h3>
                <ul className="space-y-4">
                  {[
                    'Easy access from Mumbai & Pune',
                    'Close to major tourist attractions',
                    'High weekend footfall',
                    'Strong rental ecosystem',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-red-100">
                      <MapPin className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══════════ FAQ ═══════════ */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 text-center mb-16">
              Your Questions, Answered
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <FAQ />
          </FadeIn>
        </div>
      </section>

      {/* ═══════════ CTA BANNER ═══════════ */}
      <section
        className="relative py-20 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        }}
      >
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Start Earning from Your Investment in Lonavala
            </h2>
            <p className="text-lg text-blue-200 mb-8">
              Limited pre-launch opportunity. Early investors benefit the most.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={scrollToForm}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg flex items-center gap-2 shadow-xl hover:scale-[1.02] transition-all duration-300"
              >
                <CalendarCheck className="w-5 h-5" />
                Book Your Site Visit Now
              </button>
              <a
                href="tel:+919867300996"
                className="bg-white/10 backdrop-blur border border-white/20 text-white px-8 py-4 rounded-lg font-bold text-lg flex items-center gap-2 hover:bg-white/20 transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                Call for Details
              </a>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-white/70 text-sm">
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" /> +919867300996
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" /> Lonavala, Maharashtra
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════ FORM SECTION ═══════════ */}
      <section id="lonavala-form" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left — info */}
            <FadeIn>
              <div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                  Get Investment Details / Book Site Visit
                </h2>
                <p className="text-gray-500 text-lg mb-10">
                  Our team will contact you shortly.
                </p>

                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                      <Phone className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Call for Details</p>
                      <a href="tel:+919867300996" className="text-lg font-bold text-gray-900 hover:text-red-600 transition-colors">
                        +919867300996
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="text-lg font-bold text-gray-900">Lonavala, Maharashtra</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Right — form */}
            <FadeIn delay={0.15}>
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 lg:p-10">
                <>
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-800 font-medium">
                      Something went wrong. Please try again or call us directly.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="e.g. Mumbai"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Investment Interest *
                    </label>
                    <select
                      name="investment_interest"
                      value={formData.investment_interest}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors bg-white"
                    >
                      <option>Rental Income Only</option>
                      <option>Dual Income (Rent + Farming)</option>
                      <option>Land + Cottage</option>
                      <option>Fractional Ownership</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Budget Range *
                    </label>
                    <select
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors bg-white"
                    >
                      <option>Rs.25 Lakh-Rs.50 Lakhs</option>
                      <option>Rs.50 Lakh-Rs.1 CR</option>
                      <option>Rs.1 CR to Rs.2CR</option>
                      <option>Above Rs.2 CR</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-lg font-bold text-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'SUBMIT'
                    )}
                  </button>

                  <p className="text-center text-gray-400 text-sm">
                    Our team will contact you shortly
                  </p>
                </form>
                </>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LonavalaPage;
